<script context="module">
    import { env } from '../Environment.svelte';

    const VERIFICATION_CODE_BASE_URL = `${env[env.stage].api.url}portalOc/WSVeL/webservices/izzi`;

    const headers = {
        'Content-Type': 'application/json',
        oferta: 'IZZI',
        origin: 'PORTALVL',
    };

    const CONSTANTS = {
        OFFER_TYPE: 'IZZI',
        ORIGIN: 'PORTALVL',
        IZZI_MOVIL_IDENTIFIER: ['movil', 'plan familiar principal'],
    };

    const ERROR_CODES = {
        SUCCESS: '000',
        INVALID_CODE: '001',
        DENIED_WHATSAPP: '100',
        NOT_STATUS_WHATSAPP: '101',
    };

    function validateStore(store) {
        const requiredPaths = [
            'personalDetails.correo',
            'personalDetails.numeroCelular',
            'personalDetails.nombre',
            'personalDetails.apPaterno',
            'requestedServices.product',
            'requestedServices.channelExtras',
            'requestedServices.promotions',
            'requestedServices.izzimovil',
            'requestedServices.total',
        ];

        for (const path of requiredPaths) {
            const parts = path.split('.');
            let current = store;

            for (const part of parts) {
                if (
                    current === undefined ||
                    current === null ||
                    !(part in current)
                ) {
                    throw new Error(`Invalid store object: missing ${path}`);
                }
                current = current[part];
            }
        }

        return true;
    }

    function generateTransactionId(store) {
        const email = store.personalDetails.correo || '';
        const phone = store.personalDetails.numeroCelular || '';
        const name = store.personalDetails.nombre || '';
        const lastName = store.personalDetails.apPaterno || '';

        const timestamp = Date.now().toString();

        const randomPart = Math.random().toString(36).substring(2, 10);

        const combinedString = `${email}-${phone}-${name}-${lastName}-${timestamp}-${randomPart}`;

        let hash = 0;
        for (let i = 0; i < combinedString.length; i++) {
            const char = combinedString.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash;
        }

        const hexHash = Math.abs(hash).toString(16);

        return `${timestamp.slice(-6)}-${hexHash}`;
    }

    function getPromos(store) {
        return {
            promos: store.requestedServices.promotions.map((promo) => ({
                name: promo.promoName,
                amount: Math.abs(Number(promo.promoPrice)).toString(),
            })),
            totalCostPromos: store.requestedServices.promotions
                .reduce(
                    (total, promo) =>
                        total + Math.abs(Number(promo.promoPrice)),
                    0
                )
                .toString(),
        };
    }

    function getAddons(store) {
        return {
            addons: store.requestedServices.channelExtras.map((addon) => ({
                name: addon.extra.title,
                price: addon.extra.cost.toString(),
            })),
            totalCostAddons: store.requestedServices.channelExtras
                .reduce((total, addon) => total + Number(addon.extra.cost), 0)
                .toString(),
        };
    }

    function getMobile(store) {
        return {
            priceMobile: store.requestedServices.izzimovil
                .reduce(
                    (total, mobile) =>
                        total +
                        Number(mobile.plan?.extra?.cost || mobile.plan.cost),
                    0
                )
                .toString(),
            promoMobile: getPromos(store)
                .promos.reduce((total, promo) => {
                    if (
                        CONSTANTS.IZZI_MOVIL_IDENTIFIER.some((identifier) =>
                            promo.name
                                .normalize('NFD')
                                .replace(/[\u0300-\u036f]/g, '')
                                .toLowerCase()
                                .includes(identifier)
                        )
                    ) {
                        return total + Number(promo.amount);
                    }
                    return total;
                }, 0)
                .toString(),
        };
    }

    function buildRequestVerificationCodePayload(store, deliveryMethod) {
        validateStore(store);
        return {
            oferta: 'IZZI',
            medio: deliveryMethod,
            idTransaction: generateTransactionId(store),
            telefono: store.personalDetails.numeroCelular,
            phone: store.personalDetails.numeroCelular,
            mail: store.personalDetails.correo,
            name: store.personalDetails.nombre,
            lastname: store.personalDetails.apPaterno,
            package: store.requestedServices.product.title,
            price: store.requestedServices.product.cost.toString(),
            priceAddons: getAddons(store).totalCostAddons,
            priceWithoutPromo: (
                Number(store.requestedServices.total) +
                Number(getPromos(store).totalCostPromos)
            ).toString(),
            priceWithPromo: store.requestedServices.total.toString(),
            priceMobile: getMobile(store).priceMobile,
            promoMobile: getMobile(store).promoMobile,
            addons: getAddons(store).addons,
            promos: getPromos(store).promos,
        };
    }

    function buildRequestOptions(payload) {
        const requestHeaders = { ...headers };
        if (payload.medio) requestHeaders['medio'] = payload.medio;
        return {
            method: 'POST',
            headers: requestHeaders,
            body: JSON.stringify(payload),
        };
    }

    async function makeApiRequest(endpoint, payload) {
        try {
            const response = await fetch(
                `${VERIFICATION_CODE_BASE_URL}/${endpoint}`,
                buildRequestOptions(payload)
            );

            if (!response.ok) {
                const errorText = await response
                    .text()
                    .catch(() => 'Unknown error');
                throw new Error(
                    `HTTP error! status: ${response.status}, details: ${errorText}`
                );
            }

            return await response.json();
        } catch (error) {
            console.error(`API request to ${endpoint} failed:`, error);
            throw error;
        }
    }

    export async function getVerificationCode(store, deliveryMethod) {
        const payload = buildRequestVerificationCodePayload(
            store,
            deliveryMethod
        );
        const { idTransaction } = payload;
        const apiResponse = await makeApiRequest('envio_codigo_v2', payload);

        return {
            ...apiResponse,
            transactionId: idTransaction,
        };
    }

    export async function validateVerificationCode(payload) {
        return makeApiRequest('verif_codigo_v2', payload);
    }

    export { ERROR_CODES };
</script>
